import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Mail, Lock, Phone, User, LogIn, Sparkles, Smartphone, ArrowRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const AuthModal = () => {
  const {
    isAuthModalOpen,
    closeAuthModal,
    loginWithGoogle,
    login,
    signup,
    verifyEmail,
    resendVerificationEmail,
    sendOtp,
    verifyOtp,
    initMsg91Widget,
    isPhoneMock,
  } = useAuth();

  const [activeTab, setActiveTab] = useState('email'); // 'email' | 'phone'
  const [isSignup, setIsSignup] = useState(false);

  // Email form fields
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  
  // OTP flow states
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
 
  // Phone OTP tab states
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
 
  // Global countdown & loaders
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
 
  // Reset state on open/close or tab change
  useEffect(() => {
    setError(null);
    setSuccess(null);
    setLoading(false);
    setEmail('');
    setName('');
    setPhone('');
    setEmailOtp('');
    setEmailOtpSent(false);
    setPhoneOtp('');
    setPhoneOtpSent(false);
    setCountdown(0);
  }, [isAuthModalOpen, activeTab, isSignup]);

  // Countdown timer for OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  if (!isAuthModalOpen) return null;

  const handleResendEmailOtp = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await resendVerificationEmail(email);
      setCountdown(30);
      setSuccess('Verification code resent to your email!');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (isSignup) {
      if (!emailOtpSent) {
        if (!name.trim()) {
          setError('Please enter your name');
          return;
        }
        setLoading(true);
        try {
          await signup(email, name);
          setEmailOtpSent(true);
          setCountdown(30);
          setSuccess('Verification code sent to your email!');
        } catch (err) {
          setError(err.response?.data?.detail || err.message || 'Signup failed');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (emailOtp.length < 6) {
        setError('Please enter the  verification code');
        return;
      }

      setLoading(true);
      try {
        await verifyEmail(email, emailOtp);
        setSuccess('Account created and verified successfully!');
        setTimeout(() => closeAuthModal(), 1000);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Verification failed');
      } finally {
        setLoading(false);
      }
    } else {
      if (emailOtpSent) {
        if (emailOtp.length < 6) {
          setError('Please enter verification code');
          return;
        }
        setLoading(true);
        try {
          await verifyEmail(email, emailOtp);
          setSuccess('Email verified and logged in successfully!');
          setTimeout(() => closeAuthModal(), 1000);
        } catch (err) {
          setError(err.response?.data?.detail || err.message || 'Verification failed');
        } finally {
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        await login(email);
        setEmailOtpSent(true);
        setCountdown(30);
        setSuccess('Verification code sent to your email!');
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail === 'email_unverified') {
          setEmailOtpSent(true);
          setCountdown(30);
          setSuccess('Your email is unverified. A new verification code was sent!');
        } else {
          setError(detail || err.message || 'Email authentication failed');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setSuccess(null);

    if (isSignup && !name.trim()) {
      setError('Please enter your name');
      return;
    }

    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    const initialized = initMsg91Widget && initMsg91Widget(
      phoneDigits,
      async (data) => {
        console.log('MSG91 Widget raw success data:', data);
        const token = data?.message || data?.token || data?.jwt_token || (typeof data === 'string' ? data : null);
        try {
          await verifyOtp(phoneDigits, null, isSignup ? name : null, token);
          setSuccess(isSignup ? 'Registered & logged in successfully!' : 'OTP verified! Logged in successfully.');
          setTimeout(() => closeAuthModal(), 1000);
        } catch (err) {
          setError(err.response?.data?.detail || err.message || 'Verification failed');
        }
      },
      (err) => {
        console.warn('MSG91 Widget error:', err);
      }
    );

    if (initialized) {
      setSuccess('OTP send Successfully.');
      setPhoneOtpSent(true);
      setLoading(false);
      return;
    }

    try {
      await sendOtp(phoneDigits);
      setPhoneOtpSent(true);
      setCountdown(30);
      setSuccess(isPhoneMock ? 'OTP sent! Use 123456 for testing' : 'OTP sent successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (phoneOtp.length < 4) {
      setError('Please enter the valid OTP');
      return;
    }

    setLoading(true);
    try {
      const phoneDigits = phone.replace(/\D/g, '');
      await verifyOtp(phoneDigits, phoneOtp, isSignup ? name : null);
      setSuccess(isSignup ? 'Registered & logged in successfully!' : 'OTP verified! Logged in successfully.');
      setTimeout(() => closeAuthModal(), 1000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        {/* Backdrop overlay listener */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeAuthModal}
          className="absolute inset-0 cursor-pointer"
        />

        {/* Modal Card content */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', duration: 0.4 }}
          className="relative w-full max-w-md overflow-hidden bg-bgmain rounded-3xl shadow-2xl z-10"
        >
          {/* Close trigger button */}
          <button
            onClick={closeAuthModal}
            className="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white/20 transition-colors z-20"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Heading Logo Banner */}
          <div className="bg-gradient-hero p-6 text-white text-center">
            <div className="w-12 h-12 rounded-full bg-gradient-offer flex items-center justify-center mx-auto shadow-md mb-2">
              <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <h2 className="font-heading font-bold text-2xl font-bold">Pushpanjali</h2>
            <p className="text-white/80 text-xs font-body mt-1">Enjoy authentic homemade tiffin & catering</p>
          </div>

          <div className="p-6">
            {/* Notification messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded text-red-700 text-sm font-body">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 rounded text-green-700 text-sm font-body flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                {success}
              </div>
            )}

            {/* Visual State Title */}
            <div className="text-center mb-5">
              <h3 className="font-heading font-bold text-lg text-textprimary uppercase tracking-wide">
                {emailOtpSent || phoneOtpSent
                  ? 'Verify Your Code'
                  : activeTab === 'email'
                    ? (isSignup ? 'Sign Up' : 'Sign In')
                    : (isSignup ? 'Phone Sign Up' : 'Phone Sign In')}
              </h3>
              <p className="text-textsecondary text-xs mt-1.5 font-body">
                {emailOtpSent
                  ? `Enter the confirmation code sent to ${email}`
                  : phoneOtpSent
                    ? `Enter the confirmation code sent to +91 ${phone}`
                    : activeTab === 'email'
                      ? (isSignup ? 'Enter name and email to receive verification code' : 'Enter your email to receive verification code')
                      : (isSignup ? 'Enter name and mobile to receive verification code' : 'Enter your mobile number to receive verification code')}
              </p>
            </div>
 
            {/* Tab Swappers */}
            {!emailOtpSent && !phoneOtpSent && (
              <div className="flex bg-bgsecondary p-1 rounded-2xl mb-6">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('email');
                  }}
                  className={`flex-1 py-2 text-sm font-semibold rounded-xl font-heading transition-all ${
                    activeTab === 'email'
                      ? 'bg-white text-maroon shadow-sm'
                      : 'text-textsecondary hover:text-textprimary'
                  }`}
                >
                  Email OTP
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('phone');
                  }}
                  className={`flex-1 py-2 text-sm font-semibold rounded-xl font-heading transition-all ${
                    activeTab === 'phone'
                      ? 'bg-white text-maroon shadow-sm'
                      : 'text-textsecondary hover:text-textprimary'
                  }`}
                >
                  Phone OTP
                </button>
              </div>
            )}
 
            {/* Forms section */}
            {activeTab === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-4 font-body">
                {!emailOtpSent ? (
                  <>
                    {/* Standard Registration Fields */}
                    {isSignup && (
                      <div>
                        <label className="block text-xs font-semibold text-textsecondary mb-1">NAME</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Doe"
                            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-sm text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                            required
                          />
                        </div>
                      </div>
                    )}
 
                    <div>
                      <label className="block text-xs font-semibold text-textsecondary mb-1">EMAIL ADDRESS</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-sm text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  /* Render Email Verification OTP input */
                  <div>
                    <div className="text-center bg-bgsecondary py-3 px-4 rounded-2xl text-xs text-textsecondary mb-4 flex items-center justify-between">
                      <div className="text-left font-body">
                        <span>Verification code sent to <b>{email}</b></span>
                        {isSignup && <div className="text-[10px] text-textlight mt-0.5">Creating profile for <b>{name}</b></div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEmailOtpSent(false);
                          setEmailOtp('');
                        }}
                        className="text-saffron hover:underline font-semibold text-xs bg-transparent border-none cursor-pointer"
                      >
                        Edit Email
                      </button>
                    </div>
                    <label className="block text-xs font-semibold text-textsecondary mb-1">ENTER 6-DIGIT VERIFICATION CODE</label>
                    <input
                      type="text"
                      maxLength={6}
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="w-full px-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-center text-lg font-heading tracking-widest text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                      required
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-maroon hover:bg-maroon-dark text-white rounded-2xl font-heading font-semibold shadow-warm transition-colors flex items-center justify-center gap-2 cursor-pointer mt-6"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {emailOtpSent ? <ArrowRight className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                      {emailOtpSent ? 'Verify & Proceed' : (isSignup ? 'Sign Up' : 'Sign In')}
                    </>
                  )}
                </button>

                {/* Resend button for Email OTP */}
                {emailOtpSent && (
                  <div className="text-center mt-4">
                    {countdown > 0 ? (
                      <span className="text-xs text-textlight">Resend code in {countdown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendEmailOtp}
                        className="text-saffron hover:underline text-xs font-semibold bg-transparent border-none cursor-pointer"
                      >
                        Resend Verification Code
                      </button>
                    )}
                  </div>
                )}

                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSignup(!isSignup);
                    }}
                    className="text-saffron hover:underline text-xs font-medium bg-transparent border-none cursor-pointer"
                  >
                    {isSignup
                      ? 'Already have an account? Sign In'
                      : "Don't have an account? Sign Up"}
                  </button>
                </div>
              </form>
            ) : (
              /* Phone OTP Section */
              <div className="font-body">
                {!phoneOtpSent ? (
                  <form onSubmit={handleSendOtp} className="space-y-4">
                    {/* Ask for Name in Phone Tab - Sign Up mode */}
                    {isSignup && (
                      <div>
                        <label className="block text-xs font-semibold text-textsecondary mb-1">NAME</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="John Doe"
                            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-sm text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                            required
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-textsecondary mb-1">PHONE NUMBER</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
                        <span className="absolute left-9 top-1/2 -translate-y-1/2 text-sm text-textsecondary font-medium">+91</span>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="9999999999"
                          maxLength={15}
                          className="w-full pl-20 pr-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-sm text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-maroon hover:bg-maroon-dark text-white rounded-2xl font-heading font-semibold shadow-warm transition-colors flex items-center justify-center gap-2 cursor-pointer mt-6"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Smartphone className="w-4 h-4" />
                          Send Verification OTP
                        </>
                      )}
                    </button>

                    {/* Toggle link for Phone login / signup */}
                    <div className="text-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setIsSignup(!isSignup);
                        }}
                        className="text-saffron hover:underline text-xs font-medium"
                      >
                        {isSignup
                          ? 'Already have phone account? Sign In'
                          : "Don't have phone account? Sign Up"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="text-center bg-bgsecondary py-3 px-4 rounded-2xl text-xs text-textsecondary mb-4 flex items-center justify-between">
                      <div className="text-left">
                        <span>OTP sent to <b>+91 {phone}</b></span>
                        {isSignup && name && (
                          <div className="text-[10px] text-textlight mt-0.5">Registering name: <b>{name}</b></div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPhoneOtpSent(false);
                          setPhoneOtp('');
                        }}
                        className="text-saffron hover:underline font-semibold text-xs"
                      >
                        Edit
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-textsecondary mb-1">ENTER 6-DIGIT OTP</label>
                      <input
                        type="text"
                        maxLength={6}
                        value={phoneOtp}
                        onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder={isPhoneMock ? "123456" : "Enter OTP"}
                        className="w-full px-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-center text-lg font-heading tracking-widest text-textprimary transition-colors focus:ring-1 focus:ring-maroon"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-maroon hover:bg-maroon-dark text-white rounded-2xl font-heading font-semibold shadow-warm transition-colors flex items-center justify-center gap-2 cursor-pointer mt-6"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <ArrowRight className="w-4 h-4" />
                          Verify & Proceed
                        </>
                      )}
                    </button>

                    <div className="text-center mt-4">
                      {countdown > 0 ? (
                        <span className="text-xs text-textlight">Resend OTP in {countdown}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSendOtp(null)}
                          className="text-saffron hover:underline text-xs font-semibold"
                        >
                          Resend OTP Code
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Social Google Split line */}
            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-borderbeige" /></div>
              <span className="relative bg-bgmain px-3 text-xs text-textlight font-body font-medium">Or continue with</span>
            </div>

            {/* Google Sign In button */}
            <button
              onClick={loginWithGoogle}
              type="button"
              className="w-full py-2.5 border border-borderbeige hover:border-textlight hover:bg-bgsecondary rounded-2xl transition-all flex items-center justify-center gap-3 bg-white font-heading font-medium text-sm text-textprimary cursor-pointer shadow-soft"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" width="24" height="24">
                <g transform="matrix(1, 0, 0, 1, 0, 0)">
                  <path d="M21.35,11.1H12v2.7h5.38C17,15.11 15.62,16.5 13.9,17.4V19.2h2.88c2.61,-2.4 4.12,-6 4.12,-10C20.9,10.2 20.62,9.6 21.35,11.1Z" fill="#4285F4" />
                  <path d="M12,21c2.43,0 4.47,-0.81 5.96,-2.2l-2.88,-1.8c-0.81,0.54 -1.89,0.9 -3.08,0.9 -2.34,0 -4.32,-1.59 -5.04,-3.72H3.97v1.89C5.45,19.08 8.44,21 12,21Z" fill="#34A853" />
                  <path d="M6.96,14.18C6.78,13.64 6.66,13.08 6.66,12.5c0,-0.58 0.12,-1.14 0.3,-1.68V8.93H3.97C3.35,10.08 3,11.39 3,12.5c0,1.11 0.35,2.42 0.97,3.57l2.99,-1.89Z" fill="#FBBC05" />
                  <path d="M12,6.6c1.32,0 2.51,0.45 3.44,1.35l2.58,-2.58C16.46,3.92 14.43,3 12,3c-3.56,0 -6.55,1.92 -8.03,4.93l2.99,1.89c0.72,-2.13 2.7,-3.72 5.04,-3.72Z" fill="#EA4335" />
                </g>
              </svg>
              Sign in with Google
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
